import React, { useEffect, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useEpicsQuery } from '../api/use-epics-query'
import { useJiraIntegrationPage } from './jira-integration-page.provider'
import { Epic } from '../api/shared'
import { transformFormDataToBugtrackerRequest } from '../../bugtrackers/api/build-api-payload-bugtracker-credentials'
import { JiraIntegrationForm } from './jira-form-schema'
import { TypeAheadDropdown } from './type-ahead-dropdown'
import { JiraFormFieldTypes } from './jira-form-field-types'

const NO_DEFAULT_EPIC = {
  key: 'No default epic',
  id: undefined,
  fields: []
}

export const DefaultEpicField = ({ bugtracker }) => {
  const { formatMessage } = useIntl()

  const epicToString = (epic: Epic) => {
    if (!epic?.fields) {
      return formatMessage({
        id: 'detail_integrationsSettings_noDefaultEpic'
      })
    }
    const epicNameField = epic.fields.find((field) => field.key === 'epic_name')
    if (!epicNameField) {
      return formatMessage({
        id: 'detail_integrationsSettings_noDefaultEpic'
      })
    }
    return `${epicNameField.name} (${epic.key})`
  }

  const {
    state: { organizationUuid }
  } = useJiraIntegrationPage()
  const { getValues, watch } = useFormContext<JiraIntegrationForm>()

  const watchProject = watch(JiraFormFieldTypes.PROJECT)
  const [epicSearch, setEpicSearch] = useState<string>('')
  const [dropdownHidden, setDropdownHidden] = useState<boolean>(true)
  const [textInput, setTextInput] = useState<string>('')

  const { epics } = useEpicsQuery({
    organizationUuid,
    bugtrackerRequest: transformFormDataToBugtrackerRequest(getValues),
    projectKey:
      watchProject && typeof watchProject === 'object' ? watchProject.key : '',
    bugtrackerId: bugtracker?.bugtracker_id,
    epicSearch
  })

  useEffect(() => {
    if (epics?.length) {
      if (epicSearch) {
        setDropdownHidden(false)
      } else {
        setDropdownHidden(true)
        const selectedEpic: Epic | undefined = epics.find((epic) => {
          // eslint-disable-next-line unicorn/prefer-ternary
          if (value && typeof value === 'object') {
            return epic?.key === value.key
          } else {
            return epic?.id === value
          }
        })
        setTextInput(
          selectedEpic
            ? epicToString(selectedEpic)
            : formatMessage({
                id: 'detail_integrationsSettings_noDefaultEpic'
              })
        )
        onChange(selectedEpic || NO_DEFAULT_EPIC)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epics])

  const {
    field: { onChange, value }
  } = useController({ name: JiraFormFieldTypes.EPIC_ID })

  const onInputChange = (e) => {
    setTextInput(e)
    setEpicSearch(e)
  }

  useEffect(() => {
    // when projects changes we should clear out epic search
    setEpicSearch('')
  }, [watchProject])

  const suggestionClicked = (epic: Epic) => {
    onChange(epic)
    setTextInput(epicToString(epic))
    setDropdownHidden(true)
  }

  const outsideDropdownActivity = () => {
    // Set input back to previous value
    setTextInput(epicToString(value) ?? '')
    setDropdownHidden(true)
  }

  const TextInputProps = {
    value: textInput,
    label: formatMessage({
      id: 'title_integrationsSettings_defaultEpic'
    }),
    onChange: onInputChange,
    disabled: watchProject === undefined,
    onFocus: () => {
      setTextInput('')
      setDropdownHidden(false)
    }
  }
  return (
    <TypeAheadDropdown
      TextInputProps={TextInputProps}
      hidden={dropdownHidden}
      values={epics ? [NO_DEFAULT_EPIC, ...epics] : []}
      onSelectionClicked={suggestionClicked}
      onOutsideActivity={outsideDropdownActivity}
      itemToString={(epic) => epicToString(epic)}
      itemToKey={(epic) => epic.key}
      testId="default-epic"
    />
  )
}
